// ─────────────────────────────────────────────────────────────────────────────
// The returns desk (/returns/accept) — the PHYSICAL side only (Fernando 22.10: "We want to accept returns and enter them
// into our system … All the tracking should be done by the WMS, the virtual side, and we need someone to do the
// physical work"). Scan the delivery note or the label → scan each piece (a scanner is told from typing by its speed
// and its Enter; a double read of one pull counts once; a line never goes above what can come back; tap a count to
// type it; undo the last scan) → Confirm → the screen says where each piece goes: Polishing or the Defects box.
// No money, no Refund / Exchange / Nothing, no "why": what each piece IS is decided by the WMS (POST /desk/confirm →
// returns._piece_policies, _cod_verdict). Until 28.10 this page was the CS composer and the worker chose per piece,
// Refund by default — how #1481's exchange piece got refunded. The composer stays CS's tool on the order panel.
// On a P&P station the worker signs in with their badge or PIN (every step is recorded under the person); the words
// are in their language (EN/ET/RU). docs/returns/README.md "The returns desk". -- Claude, 2026-10-28
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch, API } from '../api';
import { OrderNo } from '../OrderNo';
import { fmtWh } from '../time';
import { makeScanGuard } from '../scanGuard';
import { scanSound } from '../scanSound';
import { returnsText } from '../returnsText';
import { titleEn } from '../lineName';
import { backOf } from '../ReturnComposer';
import IdentifyBox, { Identity, isBadgeCode } from '../pp2/IdentifyBox';

const R = `${API}/returns`;

type Who = { station: boolean; person: string | null; name?: string; lang?: string };
type Flash = { tone: 'ok' | 'err' | 'info'; text: string } | null;
// counts per order line (the line SOLD) → per piece variant (the line's own, or the garment packing booked on it)
type Counts = Record<string, Record<string, number>>;

const norm = (s: any) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const same = (a: any, b: any) => {
  const x = norm(a), y = norm(b);
  return !!x && !!y && (x === y || x.replace(/^0+/, '') === y.replace(/^0+/, ''));
};
const remainingOf = (l: any) => Math.max(0, Number(l.remaining ?? l.ordered_qty ?? 0));
const total = (c: Counts, line: string) => Object.values(c[line] || {}).reduce((a, n) => a + n, 0);
const newRequestId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Which piece of the order a scanned barcode is: the line's own barcode / article number first (an ordered
 *  variant is its own line), then a garment packing booked on a line instead of the line's variant. */
function matchPiece(order: any, code: string): { line: any; vid: string } | null {
  for (const l of order?.lines || []) {
    if (same(code, l.ean) || same(code, l.product_ref?.ean) || same(code, l.article_number) || same(code, l.product_code)) {
      return { line: l, vid: String(l.variation_id) };
    }
  }
  for (const l of order?.lines || []) {
    for (const s of l.shipped || []) {
      if (same(code, s.barcode) || same(code, s.ean) || same(code, s.article_number)) return { line: l, vid: String(s.variation_id) };
    }
  }
  return null;
}

/** A 401 that means "nobody is signed in at this station" — not an expired login. */
async function badgeNeeded(res: Response): Promise<boolean> {
  if (res.status !== 401) return false;
  const d = await res.clone().json().catch(() => ({}));
  return d?.detail?.code === 'badge_required';
}

const detailText = (d: any, fallback: string) =>
  typeof d?.detail === 'string' ? d.detail : d?.detail?.message || fallback;

export function ReturnsDesk() {
  const [who, setWho] = useState<Who | null>(null);
  const t = returnsText(who?.lang);
  const [askBadge, setAskBadge] = useState(false);
  const [mode, setMode] = useState<'scan' | 'count' | 'done' | 'finder'>('scan');
  const [order, setOrder] = useState<any | null>(null);
  const [counts, setCounts] = useState<Counts>({});
  const [history, setHistory] = useState<{ line: string; vid: string }[]>([]);
  const [flash, setFlash] = useState<Flash>(null);
  const [candidates, setCandidates] = useState<any[] | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState<any[]>([]);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [editLine, setEditLine] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [finderEans, setFinderEans] = useState<string[]>([]);
  const [finderCands, setFinderCands] = useState<any[] | null>(null);
  const requestId = useRef('');
  const openedAt = useRef(0);
  const guard = useRef(makeScanGuard());
  const boxRef = useRef<HTMLInputElement>(null);
  const firstKeyAt = useRef(0);
  const scanTimer = useRef<number | null>(null);

  // ── who is working (a station: the badge person; a person's own login: themselves) ──
  const loadWho = useCallback(async () => {
    const r = await authFetch(`${R}/desk/who`);
    if (!r.ok) return;
    const d: Who = await r.json();
    setWho(d);
    setAskBadge(d.station && !d.person);
  }, []);
  useEffect(() => { loadWho(); }, [loadWho]);

  const signIn = async (id: Identity): Promise<string | null> => {
    const r = await authFetch(`${API}/pp2/station/sign-in`, { method: 'POST', body: JSON.stringify({ ...id, need: 'returns_accept' }) });
    if (!r.ok) return detailText(await r.json().catch(() => ({})), 'Not accepted');
    await loadWho();
    setAskBadge(false);
    return null;
  };
  const signOut = async () => {
    await authFetch(`${API}/pp2/session/end`, { method: 'POST' });
    await loadWho();
  };

  const loadAccepted = useCallback(async () => {
    const r = await authFetch(`${R}/accepted`);
    if (r.ok) setAccepted((await r.json()).accepted || []);
  }, []);
  useEffect(() => { loadAccepted(); }, [loadAccepted]);

  const focusBox = () => setTimeout(() => boxRef.current?.focus(), 30);
  // the badge prompt reads the scanner itself — the scan box must not also take its keys (review 28.10)
  useEffect(() => { if (askBadge || editLine) boxRef.current?.blur(); else focusBox(); }, [mode, askBadge, editLine, order]);

  const say = (tone: 'ok' | 'err' | 'info', text: string, sound = true) => {
    setFlash({ tone, text });
    if (sound && tone !== 'info') scanSound(tone === 'ok' ? 'ok' : 'error');
  };

  // ── an order opens: from a scanned note/label, a chosen candidate, or the "No label?" finder ──
  const applyScan = (o: any, c: Counts, h: { line: string; vid: string }[], code: string, sound: boolean): Counts | null => {
    const m = matchPiece(o, code);
    if (!m) { if (sound) say('err', t.notOnOrder); return null; }
    const line = String(m.line.variation_id);
    const cap = remainingOf(m.line);
    if (total(c, line) >= cap) {
      if (sound) say('err', cap === 0 ? `${m.line.article_number || ''} — ${t.alreadyBack}` : t.lineFull);
      return null;
    }
    const next = { ...c, [line]: { ...(c[line] || {}), [m.vid]: ((c[line] || {})[m.vid] || 0) + 1 } };
    h.push({ line, vid: m.vid });
    if (sound) say('ok', `${m.line.article_number || titleEn(m.line)} · ${total(next, line)} / ${cap}`);
    return next;
  };

  const openOrder = (data: any, pre: string[] = []) => {
    let c: Counts = {};
    const h: { line: string; vid: string }[] = [];
    for (const code of pre) { const n = applyScan(data, c, h, code, false); if (n) c = n; }
    setOrder(data); setCounts(c); setHistory(h); setCandidates(null); setResult(null);
    requestId.current = newRequestId();
    openedAt.current = Date.now();
    setMode('count');
    setFlash(data.matched_by === 'shipping_label_partial' ? { tone: 'info', text: t.partialLabel } : null);
  };

  const lookup = async (raw: string, opts: { byId?: boolean; pre?: string[] } = {}) => {
    const code = raw.trim();
    if (!code) return;
    setCandidates(null);
    const r = await authFetch(`${R}/lookup/${encodeURIComponent(code)}${opts.byId ? '?by=id' : ''}`);
    if (r.status === 409) {
      const d = (await r.json().catch(() => ({}))).detail || {};
      setCandidates(d.candidates || []);
      say('info', t.whichOrder, false);
      return;
    }
    if (!r.ok) { say('err', t.notFound(code)); return; }
    scanSound('ok');
    openOrder(await r.json(), opts.pre || []);
  };

  // ── the "No label?" finder: every scan is added, the search runs by itself ──
  useEffect(() => {
    if (mode !== 'finder' || finderEans.length === 0) { setFinderCands(null); return; }
    const timer = setTimeout(async () => {
      const r = await authFetch(`${R}/identify-by-items`, { method: 'POST', body: JSON.stringify({ eans: finderEans, max_age_days: 400 }) });
      if (r.ok) setFinderCands((await r.json()).candidates || []);
    }, 400);
    return () => clearTimeout(timer);
  }, [finderEans, mode]);

  // ── one scan or one typed code + Enter ──
  const handle = (raw: string) => {
    const code = raw.trim();
    if (boxRef.current) boxRef.current.value = '';
    if (!code || guard.current(code)) return;              // a double read of one pull counts once
    if (isBadgeCode(code)) return;                          // a badge is never an order or a piece
    if (mode === 'finder') {
      setFinderEans(l => [...l, code]);                    // two identical pieces are two scans
      scanSound('ok');
      return;
    }
    if (mode === 'count' && order) {
      const h = [...history];
      const n = applyScan(order, counts, h, code, true);
      if (n) { setCounts(n); setHistory(h); }
      return;
    }
    lookup(code);
  };
  const onBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // a scanner types the whole code in well under 250 ms; a person does not
    const v = e.currentTarget.value;
    if (scanTimer.current) window.clearTimeout(scanTimer.current);
    if (!v.trim()) { firstKeyAt.current = 0; return; }
    if (!firstKeyAt.current) firstKeyAt.current = Date.now();
    if (v.trim().length >= 4 && Date.now() - firstKeyAt.current < 250) {
      scanTimer.current = window.setTimeout(() => { firstKeyAt.current = 0; handle(v); }, 120);
    }
  };
  const onBoxKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (scanTimer.current) window.clearTimeout(scanTimer.current);
    firstKeyAt.current = 0;
    handle(e.currentTarget.value);
  };

  const undo = () => {
    const h = [...history];
    const last = h.pop();
    if (!last) return;
    const n = Math.max(0, ((counts[last.line] || {})[last.vid] || 0) - 1);
    setCounts({ ...counts, [last.line]: { ...(counts[last.line] || {}), [last.vid]: n } });
    setHistory(h);
    setFlash(null);
  };

  const saveEdit = (l: any) => {
    const line = String(l.variation_id);
    const n = Math.max(0, Math.min(remainingOf(l), parseInt(editValue || '0', 10) || 0));
    const vid = String(backOf(l).variation_id);             // the garment that comes back for this line
    setCounts({ ...counts, [line]: { [vid]: n } });
    setHistory(h => h.filter(x => x.line !== line));
    setEditLine(null);
  };

  const cancel = () => { setOrder(null); setCounts({}); setHistory([]); setMode('scan'); setFlash(null); };

  const confirm = async () => {
    if (!order || busy) return;
    const pieces = Object.values(counts).flatMap(byVid => Object.entries(byVid).filter(([, n]) => n > 0)
      .map(([variation_id, qty]) => ({ variation_id, qty })));
    if (pieces.length === 0) { say('err', t.nothingScanned); return; }
    setBusy(true);
    try {
      const r = await authFetch(`${R}/desk/confirm`, {
        method: 'POST',
        body: JSON.stringify({ order_id: order.order_id, pieces, request_id: requestId.current,
                               accept_seconds: Math.round((Date.now() - openedAt.current) / 1000) }),
      });
      if (await badgeNeeded(r)) { setAskBadge(true); return; }
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { say('err', detailText(d, t.confirmFailed)); return; }
      setResult({ ...d, order_number: order.order_number });
      setOrder(null); setCounts({}); setHistory([]);
      setMode('done');
      setFlash(d.repeat ? { tone: 'info', text: t.repeat } : null);
      scanSound('ok');
      loadAccepted();
    } catch { say('err', t.confirmFailed); } finally { setBusy(false); }
  };

  const removeAccepted = async (id: number) => {
    if (!window.confirm(t.removeAsk)) return;
    const r = await authFetch(`${R}/accepted/${id}`, { method: 'DELETE' });
    if (await badgeNeeded(r)) { setAskBadge(true); return; }
    if (!r.ok) { say('err', detailText(await r.json().catch(() => ({})), t.confirmFailed)); return; }
    setSel(s => { const n = new Set(s); n.delete(id); return n; });
    loadAccepted();
  };

  const sendToWms = async () => {
    const ids = Array.from(sel).filter(id => accepted.some(a => a.id === id));
    if (ids.length === 0 || busy) return;
    setBusy(true);
    try {
      const r = await authFetch(`${R}/send-to-wms`, { method: 'POST', body: JSON.stringify({ accepted_ids: ids }) });
      if (await badgeNeeded(r)) { setAskBadge(true); return; }
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { say('err', detailText(d, t.confirmFailed)); return; }
      say('ok', t.created(d.code, d.lines));
      setSel(new Set());
      loadAccepted();
    } finally { setBusy(false); }
  };

  const flashCls = flash?.tone === 'ok' ? 'bg-green-900/60 border-green-600 text-green-100'
    : flash?.tone === 'err' ? 'bg-red-900/70 border-red-500 text-red-100' : 'bg-gray-800 border-gray-600 text-gray-200';
  const lines: any[] = order?.lines || [];
  const byPlace = (place: string) => (result?.pieces || []).filter((p: any) => p.place === place);

  return (
    <div className="p-6 text-gray-200 max-w-5xl mx-auto"
      onMouseDown={e => {
        const el = e.target as HTMLElement;
        if (el.closest('button') || (el as HTMLInputElement).type === 'checkbox') e.preventDefault();
      }}>
      {askBadge && (
        <IdentifyBox title={t.whoTitle} hint={t.badgeOrPin} cancelLabel={t.cancelBadge} onSubmit={signIn} onCancel={() => setAskBadge(false)} />
      )}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">{t.title}</h1>
        {who?.person && (
          <div className="text-sm text-gray-400">
            {t.working(who.name || who.person)}
            {who.station && <button onClick={signOut} className="ml-3 underline hover:text-white">{t.signOut}</button>}
          </div>
        )}
      </div>

      {/* the one box: a parcel's note or label, then its pieces */}
      <div className="flex items-center gap-3 mb-3">
        <input ref={boxRef} autoFocus onChange={onBoxChange} onKeyDown={onBoxKey}
          placeholder={mode === 'count' ? t.scanItems : mode === 'finder' ? t.noLabelHelp : t.scanPrompt}
          className="flex-1 px-4 py-4 rounded-lg bg-[#1a1d27] border-2 border-gray-700 text-xl focus:outline-none focus:border-blue-500" />
        {mode !== 'count' && (
          <button onClick={() => { setMode(mode === 'finder' ? 'scan' : 'finder'); setFinderEans([]); setFinderCands(null); setFlash(null); }}
            className={`px-4 py-4 rounded-lg border text-base whitespace-nowrap ${mode === 'finder' ? 'border-blue-500 text-white' : 'border-gray-700 text-gray-400 hover:text-white'}`}>
            {t.noLabel}
          </button>
        )}
      </div>

      {flash && <div className={`mb-4 px-4 py-3 rounded-lg border-2 text-lg font-medium ${flashCls}`}>{flash.text}</div>}

      {/* one number, several orders (two stores share a numeric namespace) */}
      {candidates && candidates.length > 0 && (
        <div className="mb-6 space-y-2">
          {candidates.map(c => (
            <button key={c.order_id} onClick={() => lookup(c.order_id, { byId: true })}
              className="w-full text-left px-4 py-3 rounded-lg border border-gray-700 hover:border-blue-500 bg-[#1a1d27]">
              <span className="text-blue-300 text-lg font-semibold mr-3">{c.order_number}</span>
              <span className="text-gray-400">{c.store_name}</span>
              <span className="text-gray-500 ml-3">{c.order_date ? fmtWh(c.order_date, 'date') : ''}</span>
            </button>
          ))}
        </div>
      )}

      {/* "No label?": scan the pieces, the matching orders appear */}
      {mode === 'finder' && (
        <div className="mb-6 bg-[#1a1d27] border border-blue-800 rounded-lg p-4">
          <div className="text-white font-semibold mb-1">{t.noLabelTitle}</div>
          <div className="text-sm text-gray-400 mb-3">{t.noLabelHelp}</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {finderEans.map((e, i) => (
              <span key={i} className="text-sm px-2 py-1 rounded bg-gray-800 text-gray-200">
                {e}<button onClick={() => setFinderEans(l => l.filter((_x, j) => j !== i))} className="ml-2 text-gray-500 hover:text-red-400">×</button>
              </span>
            ))}
          </div>
          {finderCands && finderCands.length === 0 && <div className="text-gray-400">{t.noLabelNone}</div>}
          <div className="space-y-2">
            {(finderCands || []).map(c => (
              <button key={c.order_id} onClick={() => { const pre = finderEans; setMode('scan'); lookup(c.order_id, { byId: true, pre }); }}
                className="w-full text-left px-4 py-3 rounded-lg border border-gray-700 hover:border-blue-500">
                <span className="text-blue-300 text-lg font-semibold mr-3">{c.order_number}</span>
                <span className="text-gray-400">{c.store_name}</span>
                <span className="text-gray-500 ml-3">{(c.order_date || '').slice(0, 10)}</span>
                {c.complete && <span className="ml-3 text-sm px-2 py-0.5 rounded bg-green-700 text-white">{t.allItems}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* the open parcel: one row per item, a big counter each */}
      {mode === 'count' && order && (
        <div className="mb-6 bg-[#1a1d27] border border-gray-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-800">
            <span className="text-lg">{t.order} <OrderNo value={order.order_number} className="font-semibold text-white" /></span>
            <span className="text-gray-400">{order.store_name}</span>
          </div>
          <div className="divide-y divide-gray-800">
            {lines.map(l => {
              const line = String(l.variation_id);
              const cap = remainingOf(l);
              const n = total(counts, line);
              const back = backOf(l);
              return (
                <div key={line} className={`flex items-center gap-4 px-4 py-3 ${cap === 0 ? 'opacity-40' : n > 0 ? 'bg-green-950/40' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-medium text-white">{back.article_number || l.article_number}</div>
                    <div className="text-sm text-gray-400 truncate">{titleEn(l)}{cap === 0 ? ` · ${t.alreadyBack}` : ''}</div>
                  </div>
                  {editLine === line ? (
                    <div className="flex items-center gap-2">
                      <input autoFocus type="number" min={0} max={cap} value={editValue} placeholder={t.typeCount}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Escape') setEditLine(null);
                          if (e.key !== 'Enter') return;
                          // more than 3 digits is a barcode scanned into the count field, not a count
                          if (editValue.trim().length > 3) { const v = editValue; setEditLine(null); handle(v); return; }
                          saveEdit(l);
                        }}
                        className="w-20 px-2 py-2 rounded bg-gray-900 border border-gray-600 text-2xl text-center" />
                      <button onClick={() => saveEdit(l)} className="px-3 py-2 rounded bg-blue-700 text-white">OK</button>
                    </div>
                  ) : (
                    <button disabled={cap === 0} onClick={() => { setEditLine(line); setEditValue(String(n)); }}
                      className="text-3xl font-bold tabular-nums px-3 py-1 rounded hover:bg-gray-800 disabled:hover:bg-transparent">
                      <span className={n > 0 ? 'text-green-300' : 'text-gray-500'}>{n}</span>
                      <span className="text-gray-600 text-xl"> / {cap}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-900/50">
            <button onClick={confirm} disabled={busy}
              className="px-6 py-3 rounded-lg bg-green-600 hover:bg-green-500 text-white text-lg font-semibold disabled:opacity-40">{t.confirm}</button>
            <button onClick={undo} disabled={history.length === 0}
              className="px-4 py-3 rounded-lg border border-gray-600 text-gray-300 disabled:opacity-30">{t.undo}</button>
            <button onClick={cancel} className="ml-auto px-4 py-3 rounded-lg text-gray-400 hover:text-white">{t.cancel}</button>
          </div>
        </div>
      )}

      {/* after Confirm: only where the pieces go */}
      {mode === 'done' && result && (
        <div className="mb-6 bg-[#1a1d27] border border-gray-700 rounded-xl p-4">
          <div className="text-lg mb-3">{t.order} <OrderNo value={result.order_number} className="font-semibold text-white" /> — {t.putHere}:</div>
          {(['polishing', 'defects'] as const).map(place => byPlace(place).length > 0 && (
            <div key={place} className={`mb-3 rounded-lg border-2 p-3 ${place === 'defects' ? 'border-red-500 bg-red-950/40' : 'border-green-600 bg-green-950/30'}`}>
              <div className={`text-2xl font-bold mb-2 ${place === 'defects' ? 'text-red-200' : 'text-green-200'}`}>{t.places[place]}</div>
              {byPlace(place).map((p: any, k: number) => (
                <div key={k} className="text-lg text-white">{p.qty} × {p.article_number || titleEn(p)}</div>
              ))}
            </div>
          ))}
          <div className="text-gray-400">{t.nextParcel}</div>
        </div>
      )}

      {/* the day's accepted parcels → Send to WMS makes the return order */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">{t.accepted(accepted.length)}</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setSel(sel.size === accepted.length ? new Set() : new Set(accepted.map(a => a.id)))}
              disabled={accepted.length === 0} className="px-3 py-2 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40">
              {sel.size === accepted.length && accepted.length > 0 ? t.deselectAll : t.selectAll}
            </button>
            <button onClick={sendToWms} disabled={busy || sel.size === 0}
              className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white font-medium disabled:opacity-40">{t.sendToWms(sel.size)}</button>
          </div>
        </div>
        {accepted.length === 0 ? (
          <div className="text-gray-600 py-8 text-center border border-dashed border-gray-800 rounded-lg">{t.noneAccepted}</div>
        ) : (
          <table className="w-full bg-[#1a1d27] border border-gray-800 rounded-lg overflow-hidden">
            <thead className="text-gray-500 text-left border-b border-gray-800 text-sm">
              <tr><th className="py-2 px-3 w-10"></th><th className="py-2 px-3">{t.colOrder}</th><th className="py-2 px-3">{t.colStore}</th>
                <th className="py-2 px-3 text-center">{t.colPieces}</th><th className="py-2 px-3">{t.colBy}</th><th className="py-2 px-3 w-10"></th></tr>
            </thead>
            <tbody>
              {accepted.map(a => (
                <tr key={a.id} className="border-b border-gray-800/50">
                  <td className="py-2 px-3"><input type="checkbox" checked={sel.has(a.id)}
                    onChange={() => setSel(s => { const n = new Set(s); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; })} /></td>
                  <td className="py-2 px-3 font-medium"><OrderNo value={a.order_number} /></td>
                  <td className="py-2 px-3 text-gray-400">{a.store_name}</td>
                  <td className="py-2 px-3 text-center">{a.piece_count}</td>
                  <td className="py-2 px-3 text-gray-500">{a.accepted_by}</td>
                  <td className="py-2 px-3"><button onClick={() => removeAccepted(a.id)} className="text-gray-600 hover:text-red-400">✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
